import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Calendar, Edit, Plus, Trash2 } from 'lucide-react';

import {
	useDeleteCustomRatesMutation,
	useGetCustomRatesListQuery,
} from '../api';
import { useConfigs } from '../hooks/useConfigs';
import { useAuth } from '../providers/AppAuthProvider';
import { formatRatePlanType, formatRatePlanTypes } from '../utils/utils';

import { Badge, Button, PageContent, PageHeader, Typography } from './common';
import Loader from './Loader';

interface ConfigListProps {
	onSelectConfig?: (configId: string) => void;
	onCreateNew?: () => void;
}

const ConfigList: React.FC<ConfigListProps> = ({
	onSelectConfig,
	onCreateNew,
}) => {
	const [page, setPage] = useState(1);
	const [pageSize] = useState(10);
	const navigate = useNavigate();
	const { isAdmin } = useAuth();

	const { refreshConfigs } = useConfigs();

	const handleSelectConfigInternal = (configId: string) => {
		if (onSelectConfig) {
			onSelectConfig(configId);
		} else {
			navigate(`/settings?edit=${configId}`);
		}
	};

	const handleCreateNewInternal = () => {
		if (onCreateNew) {
			onCreateNew();
		} else {
			navigate('/settings?create=true');
		}
	};

	const {
		data: configsResponse,
		isLoading,
		error,
	} = useGetCustomRatesListQuery({
		page,
		page_size: pageSize,
	});
	const [deleteConfig, { isLoading: isDeleting }] =
		useDeleteCustomRatesMutation();

	const configs = configsResponse?.data || [];
	const total = configsResponse?.total || 0;
	const totalPages = Math.ceil(total / pageSize);

	const handleDelete = async (configId: string, configName: string) => {
		if (window.confirm(`Are you sure you want to delete "${configName}"?`)) {
			try {
				await deleteConfig(configId).unwrap();
				refreshConfigs();
			} catch (error) {
				console.error('Failed to delete config:', error);
				alert('Failed to delete config. Please try again.');
			}
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center font-primary">
				<Loader size="xl" text="Loading configurations..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-20 font-primary">
				<div className="text-center">
					<div className="text-error-600 text-lg mb-4 font-primary">
						Error loading configurations
					</div>
					<p className="text-neutral-600 font-primary">
						Please try again later.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Merchant Configurations"
				subtitle="Manage your custom rate configurations and fee schedules"
				variant="title"
				actions={
					<Button
						onClick={handleCreateNewInternal}
						disabled={!isAdmin}
						variant={isAdmin ? 'primary' : 'secondary'}
						icon={Plus}
						title={
							isAdmin ? 'Create new configuration' : 'Admin access required'
						}
					>
						Create New Config
					</Button>
				}
			/>

			<PageContent className="flex flex-1 flex-col max-w-[1280px] mx-auto">
				<div className="relative flex flex-1 flex-col">
					{/* Configurations List */}
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
						<div>
							<table className="w-[100%]">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-2xl">
											Configuration Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-2xl">
											Description
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-2xl">
											Rate Plan Type
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-xs">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-lg">
											Updated
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-xs">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{configs.map((config) => (
										<tr
											key={config.id}
											className="hover:bg-gray-50 cursor-pointer"
											onClick={() => handleSelectConfigInternal(config.id!)}
										>
											<td className="px-6 py-4 max-w-2xl">
												<div className="flex items-center">
													<div className="text-sm font-medium text-gray-900">
														{config.name}
													</div>
												</div>
											</td>
											<td className="px-6 py-4 max-w-2xl">
												<div className="text-sm text-gray-900">
													{config.description}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap max-w-2xl">
												<Badge variant="info">
													{config.selected_rate_types &&
													config.selected_rate_types.length > 0
														? formatRatePlanTypes(config.selected_rate_types)
														: formatRatePlanType(config.rate_plan_type)}
												</Badge>
											</td>
											<td className="px-6 py-4 whitespace-nowrap max-w-xs">
												<Badge variant={config.is_default ? 'success' : 'gray'}>
													{config.is_default ? 'Default' : 'Custom'}
												</Badge>
											</td>

											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xl">
												<div className="flex items-center">
													<Calendar className="w-10 h-4 mr-1" />
													{config.updated_at
														? formatDate(config.updated_at)
														: 'N/A'}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
												<div className="flex items-center space-x-2">
													<Button
														variant="outline"
														size="xs"
														onClick={(e) => {
															e.stopPropagation();
															handleSelectConfigInternal(config.id!);
														}}
														disabled={!isAdmin}
														icon={Edit}
														title={
															isAdmin
																? 'Edit configuration'
																: 'Admin access required'
														}
													>
														Edit
													</Button>
													<Button
														variant="outline"
														className="text-error-600 hover:text-error-700"
														size="xs"
														onClick={(e) => {
															e.stopPropagation();
															handleDelete(config.id!, config.name);
														}}
														disabled={isDeleting || !isAdmin}
														icon={Trash2}
														title={
															isAdmin
																? 'Delete configuration'
																: 'Admin access required'
														}
													/>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Empty State */}
						{configs.length === 0 && (
							<div className="text-center py-12 flex flex-col items-center justify-center">
								<div className="text-gray-400 mb-4">
									<Plus className="w-12 h-12 mx-auto" />
								</div>
								<Typography variant="h5" className="mb-2">
									No configurations found
								</Typography>
								<Typography variant="body2" color="muted" className="mb-4">
									Get started by creating your first custom rate configuration.
								</Typography>
								<Button
									onClick={handleCreateNewInternal}
									disabled={!isAdmin}
									variant={isAdmin ? 'primary' : 'secondary'}
									icon={Plus}
									title={
										isAdmin
											? 'Create first configuration'
											: 'Admin access required'
									}
								>
									Create First Config
								</Button>
							</div>
						)}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="mt-6 flex items-center justify-between">
							<div className="text-sm text-gray-700">
								Showing {(page - 1) * pageSize + 1} to{' '}
								{Math.min(page * pageSize, total)} of {total} results
							</div>
							<div className="flex items-center space-x-2">
								<Button
									onClick={() => setPage(page - 1)}
									disabled={page === 1}
									variant="secondary"
									size="xs"
								>
									Previous
								</Button>
								<Typography variant="body2" color="muted" className="px-3 py-2">
									Page {page} of {totalPages}
								</Typography>
								<Button
									onClick={() => setPage(page + 1)}
									disabled={page === totalPages}
									variant="secondary"
									size="xs"
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</div>
			</PageContent>
		</div>
	);
};

export default ConfigList;
